import type { DataKey } from 'recharts/types/util/types';
import { Button } from '@/components/ui/button';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import type { CompetitorInfo } from '../shared/types/rate-shop-types';

interface LegendPayloadItem {
  dataKey?: DataKey<string | number>;
  value?: string;
  color?: string;
}

interface ChartLegendProps {
  payload?: LegendPayloadItem[];
  competitorInfo: CompetitorInfo[];
  visibleSeries: Record<string, boolean>;
  onLegendClick: (dataKey: string) => void;
  nonClientCompetitors: string[];
  onShowAll: () => void;
  onHideAll: () => void;
}

export const ChartLegend = ({
  payload,
  competitorInfo,
  visibleSeries,
  onLegendClick,
  nonClientCompetitors,
  onShowAll,
  onHideAll,
}: ChartLegendProps) => {
  if (!payload) return null;

  return (
    <div className='mt-4'>
      {/* Legend items */}
      <div className='flex flex-wrap gap-4 justify-center'>
        {payload
          .filter(
            (
              entry,
            ): entry is LegendPayloadItem & {
              dataKey: string | number;
              value: string;
              color: string;
            } => Boolean(entry.dataKey !== undefined && entry.value && entry.color),
          )
          .map((entry, index: number) => {
            // Convert dataKey to string for consistent handling
            const dataKeyString = String(entry.dataKey);

            // Find the competitor info to get the tooltip
            const competitorData = competitorInfo.find((comp) => comp.dataKey === dataKeyString);
            const displayName = entry.value; // This is the display name from header.title
            const tooltipText = competitorData?.tooltip;
            const needsTooltip = tooltipText && tooltipText !== displayName;

            const legendItem = (
              <div
                key={index}
                className={`flex items-center gap-2 cursor-pointer text-xs ${
                  visibleSeries[dataKeyString] ? 'opacity-100' : 'opacity-50'
                }`}
                onClick={() => onLegendClick(dataKeyString)}
              >
                <div className='w-3 h-0.5' style={{ backgroundColor: entry.color }} />
                <span>{displayName}</span>
              </div>
            );

            if (needsTooltip) {
              return (
                <TooltipProvider key={index}>
                  <Tooltip>
                    <TooltipTrigger asChild>{legendItem}</TooltipTrigger>
                    <TooltipContent>
                      <p>{tooltipText}</p>
                    </TooltipContent>
                  </Tooltip>
                </TooltipProvider>
              );
            }

            return legendItem;
          })}
      </div>

      {/* Show All / Hide All buttons */}
      <div className='flex justify-center gap-2 mt-3'>
        <Button
          variant='outline'
          size='sm'
          onClick={onShowAll}
          disabled={nonClientCompetitors.length === 0}
        >
          Show All
        </Button>
        <Button
          variant='outline'
          size='sm'
          onClick={onHideAll}
          disabled={nonClientCompetitors.length === 0}
        >
          Hide All
        </Button>
      </div>
    </div>
  );
};
